import styles from "./TrainModel.module.scss";
import VLButton from "../../../uikit/Buttons/VLButton";
import { useTriggerTraining } from "./useTriggerTraining";
import { VisibilityProps } from "../../../../hooks/useVisibility";
import { VLActionModal } from "../../../uikit/VLModal";
import BaseModal from "../../../uikit/VLModal/BaseModal";
import { Form, Radio, Switch, Tooltip } from "antd";
import { VLInput } from "../../../uikit/FormFields/VLInput/VLInput";
import { Dataset } from "../../../../types";
import SelectList, { Option } from "../../../uikit/FormFields/SelectList";
import classNames from "classnames";
import { useFeatureFlagService } from "../../../../hooks/useFeatureFlagService";
import { FLYWHEEL } from "../../../../helpers/constants/filters";
import { useEffect, useState } from "react";
import {
  CheckCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";

// Train / Validate dialogs, opened from the Model Jobs panel
export const ModelJobDialogs = ({
  isOpen,
  close,
  isTrainWorthy,
  dataset,
}: VisibilityProps & {
  isTrainWorthy: boolean;
  dataset: Dataset;
}) => {
  const {
    isLoadingModels,
    modelsOptions,
    sourceOptions,
    triggerTrainingMutation,
    triggerValidationMutation,
    modelJobType,
    setModelJobType,
    handleModelToggle,
  } = useTriggerTraining(close, isOpen, dataset.id);
  const isFlywheelEnabled = useFeatureFlagService(FLYWHEEL).enabled;
  const isTrainDisabled =
    !isFlywheelEnabled ||
    !dataset.enrichment_models?.enrichment_models?.FLYWHEEL;
  const showUnavailableModal =
    !modelsOptions?.length && modelJobType === "TRAIN";

  if (showUnavailableModal) {
    return <TriggerTrainingUnavailbleModal isOpen={isOpen} close={close} />;
  }
  if (modelJobType) {
    return (
      <TriggerTrainingModal
        isOpen={isOpen}
        close={close}
        dataset={dataset}
        isLoadingModels={isLoadingModels}
        modelsOptions={modelsOptions}
        sourceOptions={sourceOptions}
        modelJobType={modelJobType}
        triggerTrainingMutation={triggerTrainingMutation}
        triggerValidationMutation={triggerValidationMutation}
        handleModelToggle={handleModelToggle}
      />
    );
  }
  return (
    <ModelJobType
      isOpen={isOpen}
      close={close}
      selectedValue={modelJobType}
      onSelection={setModelJobType}
      isTrainDisabled={isTrainDisabled}
      isTrainWorthy={isTrainWorthy}
    />
  );
};

const FORM_ID = "trigger-training-form";
type FormValues = {
  name: string;
  datasetId: string;
  model: string;
  source?: string;
};

type sourceOptions = Option[];

const TriggerTrainingModal = ({
  isOpen,
  close,
  dataset,
  isLoadingModels,
  modelsOptions,
  sourceOptions,
  triggerTrainingMutation,
  triggerValidationMutation,
  modelJobType,
  handleModelToggle,
}: VisibilityProps & {
  dataset: Dataset;
  modelJobType: ModelJobTypes;
  sourceOptions: sourceOptions;
  handleModelToggle: (value: boolean) => void;
} & Pick<
    ReturnType<typeof useTriggerTraining>,
    | "isLoadingModels"
    | "modelsOptions"
    | "triggerTrainingMutation"
    | "triggerValidationMutation"
  >) => {
  const isValidate = modelJobType === "VALIDATE";
  const titleText = isValidate ? "New Validating Job" : "New Auto Training Job";

  const handleSubmit = ({
    model,
    source,
  }: FormValues & { source?: string }) => {
    if (isValidate) {
      triggerValidationMutation.mutate({
        body: {
          model_id: model,
          dataset_id: dataset.id,
          gt_source: source || "NONE",
        },
      });
    } else {
      triggerTrainingMutation.mutate({
        body: { dataset_id: dataset.id, model },
      });
    }
  };

  return (
    <VLActionModal
      isOpen={isOpen}
      variant="general"
      onClose={close}
      htmlType="submit"
      form={FORM_ID}
      headers={{
        title: titleText,
      }}
      actionLabel="Next"
      loading={
        triggerTrainingMutation.isPending || triggerValidationMutation.isPending
      }
    >
      <TriggerTrainingForm
        dataset={dataset}
        handleSubmit={handleSubmit}
        isLoadingModels={isLoadingModels}
        modelsOptions={modelsOptions}
        sourceOptions={sourceOptions}
        isValidate={isValidate}
        handleModelToggle={handleModelToggle}
      />
    </VLActionModal>
  );
};

const TriggerTrainingForm = ({
  isLoadingModels,
  modelsOptions,
  sourceOptions,
  dataset,
  handleSubmit,
  isValidate,
  handleModelToggle,
}: Pick<
  ReturnType<typeof useTriggerTraining>,
  "modelsOptions" | "isLoadingModels"
> & {
  dataset: Dataset;
  isValidate: boolean;
  sourceOptions: sourceOptions;
  handleSubmit: (v: FormValues) => void;
  handleModelToggle: (value: boolean) => void;
}) => {
  const [form] = Form.useForm<FormValues>();

  const handleToggleChange = (checked: boolean) => {
    handleModelToggle(checked);
    //reset select model
    form.setFieldValue("model", undefined);
  };

  return (
    <Form
      form={form}
      id={FORM_ID}
      layout="vertical"
      initialValues={{ name: dataset.display_name, datasetId: dataset.id }}
      onFinish={handleSubmit}
    >
      <Form.Item name="name" label="Dataset Name" className={styles.formItem}>
        <VLInput readOnly />
      </Form.Item>
      <Form.Item
        name="datasetId"
        label="Dataset ID"
        className={styles.formItem}
      >
        <VLInput readOnly />
      </Form.Item>
      <Form.Item
        name="source"
        label="Select Labels Source as Ground Truth"
        rules={
          isValidate ? [{ required: true, message: "Cannot be empty" }] : []
        }
        hidden={!isValidate}
        className={styles.formItem}
        initialValue={sourceOptions[0].label}
      >
        <SelectList
          showSearch={false}
          loading={isLoadingModels}
          options={sourceOptions}
          placeholder="Select"
        />
      </Form.Item>
      <Toggle isValidate={isValidate} handleSwitchChange={handleToggleChange} />
      <Form.Item
        name="model"
        label="Select Trained Model"
        rules={[{ required: true, message: "Cannot be empty" }]}
        className={styles.formItem}
      >
        <SelectList
          showSearch={false}
          loading={isLoadingModels}
          options={modelsOptions}
          placeholder="Select"
          optionRender={(option) => {
            return <ModelOption option={option} isValidate={isValidate} />;
          }}
        />
      </Form.Item>
    </Form>
  );
};

const TriggerTrainingUnavailbleModal = ({ isOpen, close }: VisibilityProps) => {
  return (
    <BaseModal
      isOpen={isOpen}
      variant="error"
      onClose={close}
      headers={{
        title: "Training service is unavailable",
      }}
      footer={
        <div className={styles.unavailableModalFooter}>
          <VLButton type="primary" text="Close" onClick={close} />
        </div>
      }
    >
      <div className={styles.unavailableModalDescription}>
        The training service is currently down, so the training cannot be
        started. Please try again later or contact support if the issue
        persists.
      </div>
    </BaseModal>
  );
};

interface ModelJobTypeProps {
  isOpen: boolean;
  close: () => void;
  selectedValue: ModelJobTypes | null;
  onSelection?: (value: ModelJobTypes | null) => void;
  isTrainDisabled: boolean;
  isTrainWorthy: boolean;
}

export const ModelJobTypeValues = {
  TRAIN: "TRAIN",
  VALIDATE: "VALIDATE",
} as const;

export type ModelJobTypes =
  (typeof ModelJobTypeValues)[keyof typeof ModelJobTypeValues];

const ModelJobType = ({
  isOpen,
  close,
  onSelection,
  isTrainDisabled,
  isTrainWorthy,
}: ModelJobTypeProps) => {
  const initialValue = isTrainDisabled
    ? ModelJobTypeValues.VALIDATE
    : ModelJobTypeValues.TRAIN;
  const [value, setValues] = useState(initialValue);

  const handleFinish = () => {
    if (onSelection) {
      onSelection(value);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setValues(initialValue);
    }
  }, [isOpen, initialValue]);
  return (
    <VLActionModal
      isOpen={isOpen}
      variant="general"
      onClose={close}
      onClick={handleFinish}
      className={styles.jobTypeModal}
      form={FORM_ID}
      headers={{
        title: "Select Model Job",
        titleClasses: styles.jobTypeTitle,
        subTitle: "Choose how you want to proceed with this dataset",
        subTitleClasses: styles.jobTypeSubtitle,
      }}
      actionLabel="Next"
    >
      <Radio.Group
        value={value}
        onChange={(e) => {
          setValues(e.target.value);
        }}
        className={styles.group}
      >
        <Radio
          value={ModelJobTypeValues.TRAIN}
          className={classNames(styles.option)}
          title="Train"
          disabled={isTrainDisabled}
        >
          <div className={styles.cardItems}>
            <div className={styles.title}>Train</div>
            <div className={styles.subTitle}>
              <div>Start a new training job using this dataset.</div>
              {(!isTrainWorthy || isTrainDisabled) && (
                <div className={styles.warning}>
                  <WarningOutlined />{" "}
                  {isTrainDisabled
                    ? `Complete Label Propagation to enable model training`
                    : `Pay attention - this dataset is not
                  train-worthy.`}
                </div>
              )}
            </div>
          </div>
        </Radio>
        <Radio
          value={ModelJobTypeValues.VALIDATE}
          className={classNames(styles.option)}
          title="Validate"
        >
          <div className={styles.cardItems}>
            <div className={styles.title}>Validate</div>
            <div className={styles.subTitle}>
              Evaluate model quality without training.
            </div>
          </div>
        </Radio>
      </Radio.Group>
    </VLActionModal>
  );
};

interface ToggleProps {
  isValidate: boolean;
  handleSwitchChange: (checked: boolean) => void;
}

const Toggle = ({ isValidate, handleSwitchChange }: ToggleProps) => {
  const switchClassname = classNames(styles.showAllModels, {
    [styles.hideToggle]: !isValidate,
  });

  return (
    <div className={switchClassname}>
      <Switch defaultChecked={true} onChange={handleSwitchChange} />
      Show only compatible models
      <Tooltip title="Compatible models match your dataset's job type, recipe, and setup configuration. Using incompatible models may lead to validation errors.">
        <div className={styles.labelInfo}>
          <InfoCircleOutlined />
        </div>
      </Tooltip>
    </div>
  );
};

interface ModelData {
  label?: string;
  name?: string;
  icon?: React.ReactNode;
  version?: string;
  accuracy?: number;
  date?: string;
  isCompatible?: boolean;
}

interface ModelOptionProps {
  option: any; // Keep as any since it's FlattenOptionData from SelectList
  isValidate: boolean;
}

const ModelOption = ({ option, isValidate }: ModelOptionProps) => {
  const item = (isValidate ? option?.data : option) as ModelData;
  const isCompatible = item?.isCompatible;
  const icon = isCompatible ? <CheckCircleOutlined /> : <WarningOutlined />;

  const iconClassName = classNames(styles.icon, {
    [styles.showCompatible]: isCompatible,
  });

  return !isValidate ? (
    <div>{item?.label}</div>
  ) : (
    <div className={styles.itemContainer}>
      <div className={styles.upperRow}>
        <div className={iconClassName}>{icon}</div>
        <div className={styles.modelTitle}>{item?.name}</div>
        {!isCompatible && <div className={styles.modelLabel}>INCOMPATIBLE</div>}
      </div>
      <div className={styles.lowerRow}>
        {item?.version && <div className={styles.version}>{item?.version}</div>}
        {item?.accuracy && (
          <div className={styles.accuracy}>{`Accuracy:${item.accuracy}%`}</div>
        )}
        <div className={styles.date}>{item?.date}</div>
      </div>
    </div>
  );
};
